import { ArrowRightOutlined } from '@ant-design/icons';
import { Button, Descriptions, Drawer, Flex, Space, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { hasBillingAccess } from './access';
import { formatDate, formatINR } from './format';
import { orderStatus, orderedTrackTasks } from './status';
import type { WorkOrder, ZenUser } from './types';
import { DueText, OrderNumber, StatusPill, TaskTrack } from './ui';
import { orderValue } from './views';

const { Text, Paragraph } = Typography;

/** 480 px side drawer for a quick look without losing the list. */
export function OrderPeek({ order, user, onClose }: { order: WorkOrder | null; user: ZenUser; onClose: () => void }) {
  const billing = hasBillingAccess(user);
  const value = order ? orderValue(order) : null;
  return (
    <Drawer
      open={Boolean(order)}
      onClose={onClose}
      size={480}
      className="ua-peek"
      title={order ? (
        <Flex vertical gap={4}>
          <OrderNumber>{order.orderNumber}</OrderNumber>
          <span>{order.customerName}</span>
        </Flex>
      ) : null}
      extra={order ? (
        <Link to={`/work-orders/${order.id}`}>
          <Button type="primary" icon={<ArrowRightOutlined />} iconPlacement="end">Open</Button>
        </Link>
      ) : null}
    >
      {order && (
        <Space orientation="vertical" size={20} style={{ width: '100%' }}>
          <Flex gap={12} align="center" wrap>
            <StatusPill status={orderStatus(order)} />
            <DueText date={order.deliveryDate} done={order.status !== 'active'} prefix="Due" />
          </Flex>
          {order.items.map((item, index) => (
            <div key={item.id} className="ua-peek-item">
              <div className="ua-overline">Item {index + 1}</div>
              <Paragraph ellipsis={{ rows: 3 }} style={{ margin: '4px 0 12px', whiteSpace: 'pre-line' }}>{item.description}</Paragraph>
              <TaskTrack tasks={orderedTrackTasks(item.tasks)} />
            </div>
          ))}
          <Descriptions size="small" column={1} colon={false}>
            <Descriptions.Item label="Order date">{formatDate(order.orderDate)}</Descriptions.Item>
            <Descriptions.Item label="Contact / designer">{order.contactPerson || '—'}</Descriptions.Item>
            <Descriptions.Item label="Created by">{order.creatorName}</Descriptions.Item>
            {order.installation.required && <Descriptions.Item label="Installation / delivery">{order.installation.details || 'Required'}</Descriptions.Item>}
            {order.repair.required && <Descriptions.Item label="Repair">{order.repair.details || 'Required'}</Descriptions.Item>}
            {billing && <Descriptions.Item label="Value">{value !== null ? formatINR(value) : '—'}</Descriptions.Item>}
            {billing && order.invoice && <Descriptions.Item label="Invoice"><span className="ua-mono">{order.invoice.number}</span></Descriptions.Item>}
            {billing && order.customerBalance !== undefined && (
              <Descriptions.Item label="Customer balance">
                <Text className={order.customerBalance > 0 ? 'ua-text-warning' : undefined}>{formatINR(order.customerBalance)}</Text>
              </Descriptions.Item>
            )}
          </Descriptions>
        </Space>
      )}
    </Drawer>
  );
}
